// TelcoTrack — Navolávání: stavy, oprávnění, výběr Připojených budov, Hotovo z milníku Zavrtaný.

import { isAcquisitionRejected } from './acquisition-report.constants';
import { ROLES } from './roles.constants';
import {
  parseWorkReservationKind,
  type WorkReservationKind,
} from './work-reservation.constants';

/** Uložené stavy (řádek v `budova_navolani`). Nevoláno = žádný řádek. Hotovo se neukládá. */
export const NAVOLAVANI_STORED_STATUSES = [
  'nedovolano',
  'zavolatvtermin',
  'domluveno',
  'odmitnuto',
] as const;
export type NavolavaniStoredStatus = (typeof NAVOLAVANI_STORED_STATUSES)[number];

/** Zobrazený stav včetně odvozeného Nevoláno a Hotovo. */
export const NAVOLAVANI_DISPLAY_STATUSES = [
  'nevolano',
  'nedovolano',
  'zavolatvtermin',
  'domluveno',
  'odmitnuto',
  'hotovo',
] as const;
export type NavolavaniDisplayStatus = (typeof NAVOLAVANI_DISPLAY_STATUSES)[number];

export const NAVOLAVANI_STATUS_LABELS_CS: Record<NavolavaniDisplayStatus, string> = {
  nevolano: 'Nevoláno',
  nedovolano: 'Nedovoláno',
  zavolatvtermin: 'Zavolat v termín',
  domluveno: 'Domluveno',
  odmitnuto: 'Odmítnuto',
  hotovo: 'Hotovo',
};

/** Ručně nastavitelné stavy v selectu (Hotovo je jen z milníku). */
export const NAVOLAVANI_EDITABLE_STATUSES = NAVOLAVANI_DISPLAY_STATUSES.filter(
  (status): status is Exclude<NavolavaniDisplayStatus, 'hotovo'> => status !== 'hotovo',
);

const STORED_STATUS_SET = new Set<string>(NAVOLAVANI_STORED_STATUSES);

export function isNavolavaniStoredStatus(value: string | null | undefined): value is NavolavaniStoredStatus {
  return STORED_STATUS_SET.has((value ?? '').trim().toLowerCase());
}

/** Domluveno = termín realizace + technik (+ Google / work_reservations). */
export function isNavolavaniAppointmentStatus(status: string | null | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'domluveno';
}

/** Zavolat v termín = termín volání (callback), bez technika a bez rezervace. */
export function isNavolavaniCallbackStatus(status: string | null | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'zavolatvtermin';
}

export function navolavaniStatusRequiresTechnician(status: string | null | undefined): boolean {
  return isNavolavaniAppointmentStatus(status);
}

export function navolavaniStatusRequiresCallbackAt(status: string | null | undefined): boolean {
  return isNavolavaniCallbackStatus(status);
}

export function navolavaniStatusRequiresAgreedAt(status: string | null | undefined): boolean {
  return isNavolavaniAppointmentStatus(status) || isNavolavaniCallbackStatus(status);
}

/** Role, které vidí menu Navolávání jako terén (kromě akvizice / admin / `navolavani`). */
export const NAVOLAVANI_TECHNICIAN_ROLES = [ROLES.MONTAZ, ROLES.OPTIKA, ROLES.STAVBA] as const;

/** Defaultní délka termínu (Google / work_reservations), když chybí čas do. */
export const NAVOLAVANI_EVENT_DURATION_MS = 90 * 60 * 1000;

/**
 * In-app reminder for status „Zavolat v termín“ — fires this long before `agreed_at`.
 * Server poller (object-service); not a client timer.
 */
export const NAVOLAVANI_CALLBACK_REMINDER_LEAD_MS = 15 * 60 * 1000;

/** Still fire if the poller runs slightly after `agreed_at` (brief downtime). */
export const NAVOLAVANI_CALLBACK_REMINDER_LATE_GRACE_MS = 15 * 60 * 1000;

/** In-app notification type / phase for callback reminders (workflow-service bell). */
export const NAVOLAVANI_CALLBACK_REMINDER_OBJECT_TYPE = 'NAVOLAVANI';
export const NAVOLAVANI_CALLBACK_REMINDER_PHASE_CODE = 'CALLBACK_REMINDER';

/** True when a callback reminder should exist for this stored status + time. */
export function shouldScheduleNavolavaniCallbackReminder(
  status: string | null | undefined,
  agreedAt: string | Date | null | undefined,
): boolean {
  if (!isNavolavaniCallbackStatus(status)) return false;
  const raw = agreedAt instanceof Date ? agreedAt.toISOString() : String(agreedAt ?? '').trim();
  return Number.isFinite(Date.parse(raw));
}

export function navolavaniCallbackReminderWindow(agreedAtMs: number): {
  openAtMs: number;
  closeAtMs: number;
} {
  return {
    openAtMs: agreedAtMs - NAVOLAVANI_CALLBACK_REMINDER_LEAD_MS,
    closeAtMs: agreedAtMs + NAVOLAVANI_CALLBACK_REMINDER_LATE_GRACE_MS,
  };
}

/** Whether `now` is inside the fire window for a given callback time. */
export function isNavolavaniCallbackReminderDue(
  agreedAt: Date | string | number,
  nowMs: number = Date.now(),
): boolean {
  const agreedMs =
    typeof agreedAt === 'number' ? agreedAt : new Date(agreedAt).getTime();
  if (!Number.isFinite(agreedMs)) return false;
  const { openAtMs, closeAtMs } = navolavaniCallbackReminderWindow(agreedMs);
  return nowMs >= openAtMs && nowMs <= closeAtMs;
}

export function formatNavolavaniCallbackReminderMessage(params: {
  address?: string | null;
  agreedAt: Date | string;
}): string {
  const place = String(params.address ?? '').trim() || 'budova';
  const agreed = params.agreedAt instanceof Date ? params.agreedAt : new Date(params.agreedAt);
  const when = Number.isFinite(agreed.getTime())
    ? agreed.toLocaleString('cs-CZ', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  const head = `Připomínka: zavolat v termín — ${place}`;
  return when ? `${head} (${when})` : head;
}

/** Stav / termín / kalendářové Přidat — akvizice, role Navolávání, admin. */
const EDIT_ROLES = new Set<string>([ROLES.AKVIZICE, ROLES.NAVOLAVANI, ROLES.ADMIN, ROLES.SUPERADMIN]);
const MENU_ROLES = new Set<string>([...EDIT_ROLES, ...NAVOLAVANI_TECHNICIAN_ROLES]);
const GOOGLE_ROLES = new Set<string>([ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.NAVOLAVANI]);

function normalizeRoles(roles: string[] | undefined | null): string[] {
  return (roles ?? []).map((role) => String(role).toLowerCase().trim()).filter(Boolean);
}

export function canViewNavolavaniMenu(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((role) => MENU_ROLES.has(role));
}

export function canEditNavolavani(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((role) => EDIT_ROLES.has(role));
}

export function canManageNavolavaniGoogle(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((role) => GOOGLE_ROLES.has(role));
}

/** Primární kalendář Google (nelze smazat z Nastavení). */
export function isPrimaryNavolavaniGoogleCalendar(
  calendarId: string | null | undefined,
  ownerEmail?: string | null,
): boolean {
  const id = (calendarId ?? '').trim().toLowerCase();
  if (!id) return false;
  if (id === 'primary') return true;
  const owner = (ownerEmail ?? '').trim().toLowerCase();
  return !!owner && id === owner;
}

export function isNavolavaniTechnicianRoles(roles: string[] | undefined | null): boolean {
  const set = new Set(normalizeRoles(roles));
  return NAVOLAVANI_TECHNICIAN_ROLES.some((role) => set.has(role));
}

/** Připojená budova, která není zamítnutá akvizicí. */
export function isNavolavaniListBuilding(input: {
  buildingType?: string | null;
  statusAcq?: string | null;
  souhlasSmlouva?: string | null;
}): boolean {
  if ((input.buildingType ?? '').trim() !== 'PRIPOJENA') return false;
  return !isAcquisitionRejected({
    statusAcq: input.statusAcq,
    souhlasSmlouva: input.souhlasSmlouva,
  });
}

export function isNavolavaniZavrtDocumented(milestones: { zavrtany?: boolean | null } | null | undefined): boolean {
  return milestones?.zavrtany === true;
}

/** Hotovo má přednost před uloženým stavem. Bez řádku = Nevoláno. */
export function resolveNavolavaniDisplayStatus(
  stored: string | null | undefined,
  zavrtDocumented: boolean,
): NavolavaniDisplayStatus {
  if (zavrtDocumented) return 'hotovo';
  const key = (stored ?? '').trim().toLowerCase();
  if (isNavolavaniStoredStatus(key)) return key;
  return 'nevolano';
}

/**
 * Stavy, které se ještě mají volat (Nevoláno / Nedovoláno / Zavolat v termín).
 * Ostatní včetně neznámých = fail-closed.
 */
export function isNavolavaniToCallStatus(status: string | null | undefined): boolean {
  const key = (status ?? '').trim().toLowerCase();
  return key === 'nevolano' || key === 'nedovolano' || key === 'zavolatvtermin';
}

export interface NavolavaniKNavolaniBuilding {
  displayStatus?: string | null;
  done?: boolean | null;
}

/**
 * K navolání: seznamová budova bez zdokumentovaného závrtu, ne Domluveno,
 * ne odmítnutá (`odmitnuto` / Hotovo). Nevoláno, Nedovoláno a Zavolat v termín ano.
 */
export function isNavolavaniKNavolaniBuilding(building: NavolavaniKNavolaniBuilding): boolean {
  if (building.done === true) return false;
  return isNavolavaniToCallStatus(building.displayStatus);
}

/** Počet budov k navolání. Hotovo (`done`) ani Domluveno / Odmítnuto se nepočítají. */
export function countNavolavaniToCall(buildings: ReadonlyArray<NavolavaniKNavolaniBuilding>): number {
  let n = 0;
  for (const building of buildings) {
    if (isNavolavaniKNavolaniBuilding(building)) n += 1;
  }
  return n;
}

/** Prefer API `kNavolaniCount`; otherwise recount from buildings. */
export function readNavolavaniKNavolaniCount(source: {
  kNavolaniCount?: number | null;
  buildings?: ReadonlyArray<NavolavaniKNavolaniBuilding>;
}): number {
  const raw = source.kNavolaniCount;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return Math.trunc(raw);
  return countNavolavaniToCall(source.buildings ?? []);
}

/** Fields on `GET /navolavani/buildings` besides `polygons[].buildings`. */
export interface NavolavaniBuildingsListCounts {
  kNavolaniCount: number;
}

export interface NavolavaniPolygonGroupCounts extends NavolavaniBuildingsListCounts {
  polygonId: string | null;
  polygonName: string;
  plannedConstructionEnd?: string | null;
}

export function navolavaniEventTitle(address: string): string {
  const place = address.trim() || 'budova';
  return `Závrt · ${place}`;
}

/** @deprecated Prefer workReservationEventTitle from work-reservation.constants. */
export function navolavaniEventTitleForKind(
  kind: 'zavrt' | 'digitalni_cesta' | 'zafuk' | 'svar_budovy',
  address: string,
): string {
  const prefixes: Record<typeof kind, string> = {
    zavrt: 'Závrt',
    digitalni_cesta: 'Digitální cesta',
    zafuk: 'Záfuk budovy',
    svar_budovy: 'Svár budovy',
  };
  const place = address.trim() || 'budova';
  return `${prefixes[kind]} · ${place}`;
}

/** Route slug + API `unassigned` for Připojené budovy without `polygon_id`. */
export const NAVOLAVANI_UNASSIGNED_POLYGON_SLUG = 'bez-polygonu';

const NAVOLAVANI_POLYGON_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isNavolavaniPolygonId(value: string | null | undefined): boolean {
  return NAVOLAVANI_POLYGON_UUID_RE.test((value ?? '').trim());
}

/**
 * Fail-closed list scope for `GET /navolavani/buildings`.
 * Unknown / malformed `polygonId` → `none` (empty), never the whole portfolio.
 */
export type NavolavaniListScope =
  | { kind: 'all' }
  | { kind: 'polygon'; polygonId: string }
  | { kind: 'unassigned' }
  | { kind: 'none' };

function isTruthyQueryFlag(value: boolean | string | null | undefined): boolean {
  if (value === true) return true;
  const raw = String(value ?? '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes';
}

export function parseNavolavaniListScope(input: {
  polygonId?: string | null;
  unassigned?: boolean | string | null;
}): NavolavaniListScope {
  const polygonId = (input.polygonId ?? '').trim();
  const unassigned = isTruthyQueryFlag(input.unassigned);
  if (unassigned && polygonId) return { kind: 'none' };
  if (unassigned) return { kind: 'unassigned' };
  if (!polygonId) return { kind: 'all' };
  if (!isNavolavaniPolygonId(polygonId)) return { kind: 'none' };
  return { kind: 'polygon', polygonId };
}

export interface NavolavaniWriteInput {
  status?: string | null;
  technicianUserId?: string | null;
  /** Start of appointment (= work_reservations.startsAt). */
  agreedAt?: string | null;
  /** Optional end (= work_reservations.endsAt). Omit/null → default +1.5h. */
  endsAt?: string | null;
  note?: string | null;
}

/** Calendar „Nový termín“ — technician + slot required; building / place optional. */
export interface NavolavaniCalendarCreateInput {
  workKind?: string | null;
  technicianUserId?: string | null;
  agreedAt?: string | null;
  endsAt?: string | null;
  note?: string | null;
  objectId?: string | null;
  /** Free-text place / address when the building is not in the app. */
  placeLabel?: string | null;
}

export type NavolavaniCalendarCreateDecision =
  | {
      ok: true;
      workKind: WorkReservationKind;
      technicianUserId: string;
      agreedAt: string;
      endsAt: string | null;
      note: string | null;
      objectId: string | null;
      placeLabel: string | null;
    }
  | { ok: false; message: string };

const PLACE_LABEL_MAX = 500;
const NOTE_MAX = 2000;

/**
 * Calendar create (Přidat / Nový termín): technician + start required.
 * Polygon / in-app building / place label are optional — free appointments allowed.
 */
export function parseNavolavaniCalendarCreate(
  input: NavolavaniCalendarCreateInput,
): NavolavaniCalendarCreateDecision {
  const workKind = parseWorkReservationKind(input.workKind) ?? 'zavrt';
  const technicianUserId = (input.technicianUserId ?? '').trim();
  const agreedRaw = (input.agreedAt ?? '').trim();
  const agreedMs = Date.parse(agreedRaw);
  if (!technicianUserId || !Number.isFinite(agreedMs)) {
    return { ok: false, message: 'Vyberte technika a termín.' };
  }
  const endsRaw = (input.endsAt ?? '').trim();
  let endsAt: string | null = null;
  if (endsRaw) {
    const endsMs = Date.parse(endsRaw);
    if (!Number.isFinite(endsMs)) {
      return { ok: false, message: 'Neplatný konec termínu.' };
    }
    if (endsMs <= agreedMs) {
      return { ok: false, message: 'Čas do musí být po čase od.' };
    }
    endsAt = new Date(endsMs).toISOString();
  }
  const noteRaw = (input.note ?? '').trim();
  if (noteRaw.length > NOTE_MAX) {
    return { ok: false, message: 'Poznámka je příliš dlouhá.' };
  }
  const placeRaw = (input.placeLabel ?? '').trim();
  if (placeRaw.length > PLACE_LABEL_MAX) {
    return { ok: false, message: 'Místo / adresa je příliš dlouhá.' };
  }
  const objectId = (input.objectId ?? '').trim() || null;
  return {
    ok: true,
    workKind,
    technicianUserId,
    agreedAt: new Date(agreedMs).toISOString(),
    endsAt,
    note: noteRaw || null,
    objectId,
    placeLabel: placeRaw || null,
  };
}

export type NavolavaniWriteDecision =
  | { ok: true; action: 'delete' }
  | {
      ok: true;
      action: 'upsert';
      status: NavolavaniStoredStatus;
      technicianUserId: string | null;
      agreedAt: string | null;
      endsAt: string | null;
      note: string | null;
    }
  | { ok: false; message: string };

/** Validace zápisu stavu. Hotovo nelze zadat ručně. Nevoláno řádek smaže. */
export function parseNavolavaniWrite(input: NavolavaniWriteInput): NavolavaniWriteDecision {
  const status = (input.status ?? '').trim().toLowerCase();
  if (status === 'hotovo') {
    return { ok: false, message: 'Hotovo se nastaví samo, jakmile je závrt zdokumentovaný.' };
  }
  if (status === 'nevolano' || status === '') {
    return { ok: true, action: 'delete' };
  }
  if (!isNavolavaniStoredStatus(status)) {
    return { ok: false, message: 'Neznámý stav navolání.' };
  }
  const noteRaw = (input.note ?? '').trim();
  if (noteRaw.length > NOTE_MAX) {
    return { ok: false, message: 'Poznámka je příliš dlouhá.' };
  }
  const note = noteRaw || null;
  if (isNavolavaniCallbackStatus(status)) {
    const agreedRaw = (input.agreedAt ?? '').trim();
    const agreedMs = Date.parse(agreedRaw);
    if (!Number.isFinite(agreedMs)) {
      return { ok: false, message: `${NAVOLAVANI_STATUS_LABELS_CS[status]} vyžaduje termín volání.` };
    }
    return {
      ok: true,
      action: 'upsert',
      status,
      technicianUserId: null,
      agreedAt: new Date(agreedMs).toISOString(),
      endsAt: null,
      note,
    };
  }
  if (!isNavolavaniAppointmentStatus(status)) {
    return {
      ok: true,
      action: 'upsert',
      status,
      technicianUserId: null,
      agreedAt: null,
      endsAt: null,
      note,
    };
  }
  const technicianUserId = (input.technicianUserId ?? '').trim();
  const agreedRaw = (input.agreedAt ?? '').trim();
  const agreedMs = Date.parse(agreedRaw);
  if (!technicianUserId || !Number.isFinite(agreedMs)) {
    return { ok: false, message: `${NAVOLAVANI_STATUS_LABELS_CS[status]} vyžaduje technika a termín realizace.` };
  }
  const endsRaw = (input.endsAt ?? '').trim();
  let endsAt: string | null = null;
  if (endsRaw) {
    const endsMs = Date.parse(endsRaw);
    if (!Number.isFinite(endsMs)) {
      return { ok: false, message: 'Neplatný konec termínu.' };
    }
    if (endsMs <= agreedMs) {
      return { ok: false, message: 'Čas do musí být po čase od.' };
    }
    endsAt = new Date(endsMs).toISOString();
  }
  return {
    ok: true,
    action: 'upsert',
    status,
    technicianUserId,
    agreedAt: new Date(agreedMs).toISOString(),
    endsAt,
    note,
  };
}
